import {
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { higginsColors } from '@higgins/ui';
import type { ChartRow, ChartFormat } from '@/lib/types';

interface LineChartProps {
  data: ChartRow[];
  index?: string;
  keys?: string[];
  unit?: ChartFormat;
}

function fmtTick(v: number, unit: ChartFormat): string {
  if (unit === 'currency') {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v}`;
  }
  if (unit === 'percent') return `${(v * 100).toFixed(0)}%`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(v);
}

function fmtTooltip(v: unknown, unit: ChartFormat): string {
  const n = v as number;
  if (unit === 'currency') {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n}`;
  }
  if (unit === 'percent') return `${(n * 100).toFixed(1)}%`;
  return n.toLocaleString();
}

export function LineChart({ data, index, keys, unit = 'number' }: LineChartProps) {
  if (data.length === 0) return null;

  const firstKey = Object.keys(data[0])[0];
  const effectiveIndex = (index && index in data[0]) ? index : firstKey;
  const seriesKeys = keys ?? Object.keys(data[0]).filter(k => k !== effectiveIndex && !k.includes('(Number)'));

  function fmtXTick(v: unknown): string {
    const s = String(v ?? '');
    const m = s.match(/\[([^\]]+)\]$/);
    if (m) return m[1].replace(/_/g, ' ');
    const parts = s.split(/[\s_-]/);
    return parts[parts.length - 1] ?? s;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ReLineChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
        <XAxis
          dataKey={effectiveIndex}
          tickFormatter={fmtXTick}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={v => fmtTick(v as number, unit)}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
          width={48}
        />
        <Tooltip
          formatter={(v: unknown) => fmtTooltip(v, unit)}
          contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e5e7eb' }}
        />
        {seriesKeys.length > 1 && (
          <Legend wrapperStyle={{ fontSize: 11 }} />
        )}
        <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={1} />
        {seriesKeys.map((key, i) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={higginsColors[i % higginsColors.length]}
            strokeWidth={2}
            dot={data.length < 15 ? { r: 3 } : false}
            activeDot={{ r: 5 }}
          />
        ))}
      </ReLineChart>
    </ResponsiveContainer>
  );
}
